import { autoUpdate, flip, offset, shift, size, useDismiss, useFloating, useInteractions } from '@floating-ui/react';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import Icon from 'components/global/Icon';
import ReactPortal from 'components/global/ReactPortal';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints, POPPER_CONFIG } from 'utils/const.styles';

import * as S from './styled/ContextMenu.styled';

/**
 * @author Burak Unsal
 */
const ContextMenu = (props) => {
	const {
		menuItems,
		row,
		anchorPoint,
		onClose,
		emptyText = 'noMenuItems',
		isContextMenuOpen,
		setIsContextMenuOpen,
		enableKeyboardNavigation = true
	} = props;

	const { showInfo } = useAlert();
	const isMobile = useMediaQuery(Breakpoints.mobile);

	const [selectedIndex, setSelectedIndex] = useState(0);

	const handleMenuItemClick = (action) => {
		if (!action) {
			showInfo({ header: 'alert.underDevelopment' });
			return;
		}
		action(row);
	};

	const middleware = useMemo(
		() => [
			flip({
				padding: 10
			}),
			shift({
				mainAxis: false
			}),
			offset({
				mainAxis: 8,
				crossAxis: 0
			}),
			size({
				apply({ availableHeight, elements, x }) {
					elements.floating.style.maxHeight = `${availableHeight}px`;
					if (isMobile) {
						const popperWidth = document.documentElement.clientWidth - 2 * POPPER_CONFIG.MARGIN;
						elements.floating.style.width = `${popperWidth}px`;
						elements.floating.style.left = `${POPPER_CONFIG.MARGIN - x}px`;
					}
				}
			})
		],
		[]
	);

	const handleOpenChange = (value) => {
		setIsContextMenuOpen(value);
		if (!value) {
			onClose();
		}
	};

	const { refs, context, floatingStyles } = useFloating({
		open: isContextMenuOpen,
		onOpenChange: handleOpenChange,
		placement: 'bottom-start',
		middleware,
		whileElementsMounted: autoUpdate
	});

	const dismiss = useDismiss(context, {
		outsidePress: onClose
	});

	const { getFloatingProps } = useInteractions([dismiss]);

	const handleClick = (e, isDisabled, action) => {
		e.preventDefault();
		e.stopPropagation();
		if (isDisabled) return;
		handleMenuItemClick(action);
		onClose();
	};

	useEffect(() => {
		refs.setPositionReference({
			getBoundingClientRect() {
				return {
					left: anchorPoint.x,
					top: anchorPoint.y,
					width: 0,
					height: 0
				};
			}
		});
	}, [refs, anchorPoint]);

	// Keyboard navigation for context menu
	const handleKeyDown = useCallback(
		(e) => {
			if (!enableKeyboardNavigation || !isContextMenuOpen || !menuItems?.length) return;

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					setSelectedIndex((prev) => (prev < menuItems.length - 1 ? prev + 1 : 0));
					break;
				case 'ArrowUp':
					e.preventDefault();
					setSelectedIndex((prev) => (prev > 0 ? prev - 1 : menuItems.length - 1));
					break;
				case 'Enter':
					e.preventDefault();
					if (menuItems[selectedIndex]) {
						handleMenuItemClick(menuItems[selectedIndex].action);
						onClose();
					}
					break;
				case 'Escape':
					e.preventDefault();
					onClose();
					break;
			}
		},
		[enableKeyboardNavigation, isContextMenuOpen, menuItems, selectedIndex, onClose]
	);

	// Reset selected index when menu opens
	useEffect(() => {
		if (isContextMenuOpen) {
			setSelectedIndex(0);
		}
	}, [isContextMenuOpen]);

	// Add keyboard event listener
	useEffect(() => {
		if (enableKeyboardNavigation && isContextMenuOpen) {
			document.addEventListener('keydown', handleKeyDown);

			return () => {
				document.removeEventListener('keydown', handleKeyDown);
			};
		}
	}, [enableKeyboardNavigation, isContextMenuOpen, handleKeyDown]);

	if (refs.reference.current === null) return null;

	return (
		<ReactPortal wrapperId={'popper'}>
			<S.ContentWrapper ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
				<S.Menu id="contextMenuContainer">
					{menuItems ? (
						menuItems.map((item, index) => {
							if (item.render && item.render(row) === false) return;

							const isDisabled = item.disabled ? item.disabled(row) : false;
							const isSelected = selectedIndex === index;

							return (
								<Tooltip title={isDisabled} placement="right" style={{ display: 'block' }} key={index}>
									<S.MenuItem
										id={item.label ? `action_${item.label}` : `action_${index}`}
										key={index}
										className={`${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
										onContextMenu={(e) => handleClick(e, isDisabled, item.action)}
										onClick={(e) => handleClick(e, isDisabled, item.action)}>
										{item.icon && <Icon icon={item.icon} />}
										<Typography>{item.label}</Typography>
									</S.MenuItem>
								</Tooltip>
							);
						})
					) : (
						<S.MenuItem className="disabled">
							<Typography>{emptyText}</Typography>
						</S.MenuItem>
					)}
				</S.Menu>
			</S.ContentWrapper>
		</ReactPortal>
	);
};

export default memo(ContextMenu);
